/**
 * <Button> — le bouton-poussoir (Buttons family).
 *
 * The maison replacement for the plain `FilledButtonM` (and the shared shell behind
 * `<LabeledIconButton>`). Brand core V13, chap. 6 : hauteur 44, rayon 12, Outfit 700 / `text-base`,
 * letter-spacing .01em, **relief de 4 px dans le ton foncé** (jamais une ombre floue), survol
 * plus clair, pressé = descend de 4 px et le relief tombe à 0, focus = anneau jaune.
 *
 * Les trois variantes du livre, sous les noms d'API d'origine :
 *  - `variant="solid"`   → **principal** : aplat `color`, relief du ton foncé, label blanc.
 *  - `variant="outline"` → **secondaire** : fond blanc, contour 2 px `border-secondary`, relief
 *    `relief-secondary`, label à la couleur de l'univers. **Blanc dans les deux thèmes** : sur la
 *    nuit, il garde sa face blanche, son contour `#D5D9E6` et son libellé marine, sur un relief
 *    gris clair (chap. 06, « MODE SOMBRE »).
 *  - `variant="ghost"`   → **tertiaire** : texte seul, souligné au survol, jamais au repos.
 *
 * **Le relief ne bouge pas la mise en page, et pressé il ne laisse rien derrière lui.** La boîte
 * extérieure est TRANSPARENTE : elle ne fait que réserver `hauteur + relief`, une fois pour
 * toutes, pour que la ligne de boutons ne tressaute pas entre repos et pressé (un `translateY`
 * ne mesure rien). Le relief est une couche à part, posée en absolu et rendue **au repos
 * seulement** ; la FACE, haute de `hauteur`, la recouvre sauf sa bande du bas. Pressé, la face
 * descend de `relief` px et la couche de relief disparaît : plus rien au-dessus ni en dessous.
 * Peindre la boîte extérieure laissait, pressé, une bande sombre de 4 px AU-DESSUS de la face.
 *
 * « Jamais un bouton jaune » : `color` exclut les jetons de signature, c'est le type qui le tient.
 * Le jaune n'entre ici que par l'anneau de focus.
 *
 * L'encre du libellé et le ton du relief ne se décident pas ici : ils appartiennent à l'aplat, et
 * se lisent sur son descripteur (`theme/tokens/colors` → `fills`).
 *
 * **Sans `color`, le bouton est celui de l'univers** (`role-fill`) : marine pour le parent, orange
 * pour l'élève, et c'est le thème qui le dit. Un appelant ne nomme une couleur que pour dire autre
 * chose que « l'action » — `danger`, `success`, ou `primary` pour rester au marine du socle.
 *
 * **Une variante dit une ACTION, jamais un état.** Le poussoir lui-même (hauteurs, relief, pressé,
 * survol, focus) est `<PushButton>`, qui peint une `PushFace` ; `<Button>` traduit sa variante
 * d'action en face, la pilule de sélection (`<ChoiceChip variant="pill">`) traduit l'état choisi /
 * non choisi en la sienne. Depuis la V16, le principal ET le secondaire sont blancs sur la nuit :
 * un `solid` / `outline` ne peut plus dire « choisi ».
 *
 * **Une action déjà faite a sa forme** (`done`) : ni aplat de l'univers, ni contour, ni relief — une
 * face plate dans la tinte du succès, son encre, et une coche en tête. Elle ne s'estompe pas quand elle
 * ne répond plus : « déjà fait » est une information, pas un bouton éteint (« Afficher à l'élève »
 * quand l'élève l'a devant lui). Toujours lisible comme un bouton par sa place et sa hauteur.
 *
 * No hex literals: every color is a `SemanticColorKey`.
 */
import React, { useContext } from "react"
import {
    ActivityIndicator,
    Dimensions,
    Pressable,
    StyleSheet as RNStyleSheet,
    View,
    type GestureResponderEvent,
} from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { DimensionsContext } from "../../../../common/utils/mobileUtils"
import {
    inkOnFill,
    outlineInkOnFill,
    reliefOnFill,
    solidFill,
    textInkOnFill,
    type SemanticColorKey,
} from "../../../theme/tokens/colors"
import { spacing } from "../../../theme/tokens/spacing"
import { FocusRing, focusRingHost } from "../../FocusRing"
import { Row } from "../../containers/Row"
import { Icon, type IconName } from "../../icons/Icon"
import { Text } from "../../typography/Text"

export type ButtonVariant = "solid" | "outline" | "ghost"
/**
 * `md` = la hauteur 44 du livre ; `sm` (32) est réservé à une barre de sélection dense ; `xs` (26, la hauteur d'une
 * chip du livre) aux actions d'une rangée de liste dense, où plusieurs se lisent d'un coup d'œil (« À traiter » du live).
 */
export type ButtonSize = "xs" | "sm" | "md"

/** Le jaune signature ne peint jamais un bouton — il n'est pas proposé. */
export type ButtonColor = Exclude<SemanticColorKey, "accent" | "accent-strong">

/**
 * `<Pressable>` ne type que `pressed` ; react-native-web ajoute `hovered` et `focused` à
 * l'exécution. Les déclarer optionnels laisse le callback accepter l'état natif tel quel.
 */
type PressableVisualState = { pressed: boolean; hovered?: boolean; focused?: boolean }

/**
 * Ce que peint un poussoir, une fois son intention traduite en jetons : la face, son contour, le
 * relief posé dessous et l'encre du libellé. Les hauteurs, le pressé et le focus ne varient pas.
 */
export type PushFace = {
    /** Aplat de la face — `null` : transparente (le tertiaire). */
    fill: SemanticColorKey | null
    /** Contour 2 px de la face — `null` : aucun. */
    border: SemanticColorKey | null
    /** Relief sous la face — `null` : aucun. */
    relief: SemanticColorKey | null
    /** Libellé et glyphe. */
    ink: SemanticColorKey
    /** « Survol : plus clair » — le voile blanc, sur un aplat seulement. */
    hoverVeil?: boolean
    /** Souligné au survol, jamais au repos — le tertiaire. */
    hoverUnderline?: boolean
    /**
     * Libellé barré : ce que la face porte est rendu, plus offert — l'heure libérée d'une semaine de
     * tuteur (`<TutorHourChip>`). Absent (défaut) : rien ne change.
     */
    strike?: boolean
    /** L'action est déjà faite : bloquée, la face ne s'estompe pas (voir `done`). */
    settled?: boolean
}

export interface ButtonProps {
    label: string
    /** `solid` (principal), `outline` (secondaire) ou `ghost` (tertiaire) — `solid` par défaut. */
    variant?: ButtonVariant
    /** Fill (solid) / label (outline, ghost) color token — défaut `role-fill`, le bouton de l'univers. */
    color?: ButtonColor
    /**
     * Label (+ icon) color token — défaut : l'encre de l'aplat (`inkOnFill`) en solid, celle du
     * secondaire (`outlineInkOnFill`) en outline, la couleur écrite (`textInkOnFill`) en ghost.
     */
    textColor?: SemanticColorKey
    /** `sm` (32) | `md` (44, défaut — la hauteur du livre). */
    size?: ButtonSize
    /** Stretch to the container width (label stays centred; icon+label rows left-align). */
    fullWidth?: boolean
    /** Fit to the content width. When false, uses a DimensionsContext-based width (menu/legacy layout). Defaults to true. */
    fitContent?: boolean
    /** Optional glyph — omit for a text-only pill. */
    iconName?: IconName
    /** Icon side — defaults to `left`. Ignored when `iconName` is omitted. */
    iconPosition?: "left" | "right"
    onPress?: (event: GestureResponderEvent) => void
    disabled?: boolean
    loading?: boolean
    testID?: string
    accessibilityLabel?: string
    /** Ce que le lecteur d'écran ajoute après le libellé (ce qui va se passer). Défaut : rien. */
    accessibilityHint?: string
    /**
     * Ce qui se peint sur la face, sous le libellé, rogné à ses coins : le remplissage d'un bouton qui
     * se presse seul (`<CountdownButton>`). Il descend avec la face quand on presse. Rien par défaut.
     */
    underlay?: React.ReactNode
    /**
     * L'action est déjà faite : la face plate du succès et sa coche, à la place de la variante (le glyphe
     * donné est remplacé). Elle répond encore si `onPress` est donné et rien ne la bloque. Défaut `false`.
     */
    done?: boolean
}

const SIZE_TOKENS: Record<
    ButtonSize,
    { height: number; paddingH: number; icon: number; text: "text-xs" | "text-sm" | "text-base"; relief: number }
> = {
    xs: { height: 26, paddingH: 10, icon: 14, text: "text-xs", relief: 2 },
    sm: { height: 32, paddingH: 12, icon: 16, text: "text-sm", relief: 3 },
    md: { height: 44, paddingH: 20, icon: 20, text: "text-base", relief: 4 },
}

/**
 * La boîte qu'un bouton à relief réserve — sa face et son relief. C'est la place que lui garde un
 * squelette ; `<ChoiceChip variant="pill">`, une face de poussoir `sm`, réserve la même.
 */
export function pushButtonBoxHeight(size: ButtonSize = "md"): number {
    return SIZE_TOKENS[size].height + SIZE_TOKENS[size].relief
}

/** La hauteur de la face seule, sans son relief : le côté d'un poussoir carré (`iconOnly`). */
export function pushButtonFaceHeight(size: ButtonSize = "md"): number {
    return SIZE_TOKENS[size].height
}

/**
 * La largeur qu'une face de poussoir prend en plus de son libellé : retrait et contour (celui du secondaire) des deux
 * côtés, et, si elle en porte un, le glyphe de tête et son écart (`gap="2"` de la rangée).
 */
export function pushButtonChromeWidth(size: ButtonSize = "md", { glyph = false }: { glyph?: boolean } = {}): number {
    const { paddingH, icon } = SIZE_TOKENS[size]
    return 2 * (paddingH + OUTLINE_WIDTH) + (glyph ? icon + GLYPH_GAP : 0)
}

/** Contour du bouton secondaire — 2 px (chap. 6). */
const OUTLINE_WIDTH = 2
/** L'écart entre le glyphe et le libellé : `spacing["2"]`, le `gap` de la rangée du libellé. */
const GLYPH_GAP = spacing["2"]
/** letter-spacing .01em du libellé `md` du livre (15 px), gardé en px fixes : il ne suit pas l'échelle. */
const LABEL_TRACKING = 0.15
/** Voile blanc du survol : « survol = plus clair », sans toucher à l'encre du libellé. */
const HOVER_VEIL_OPACITY = 0.14

/**
 * La face d'une variante d'action. Le secondaire porte TOUJOURS le contour et le relief gris du
 * livre : son ton foncé n'est pas celui de son libellé. Le principal prend l'aplat de bouton de sa
 * couleur (`solidFill` : `danger` y devient `danger-fill`, foncé chez l'élève) et le ton foncé de
 * cet aplat ; le tertiaire n'a rien. La face du secondaire est `white`, le blanc qui ne s'inverse
 * pas : sur la nuit elle reste blanche, là où `surface-raised` passerait au bleu nuit des cartes.
 */
export function actionFace(variant: ButtonVariant, color: ButtonColor, textColor?: SemanticColorKey): PushFace {
    if (variant === "solid") {
        const fill = solidFill(color)
        return {
            fill,
            border: null,
            relief: reliefOnFill(fill),
            ink: textColor ?? inkOnFill(fill),
            hoverVeil: true,
        }
    }
    if (variant === "outline") {
        return {
            fill: "white",
            border: "border-secondary",
            relief: "relief-secondary",
            ink: textColor ?? outlineInkOnFill(color),
        }
    }
    return { fill: null, border: null, relief: null, ink: textColor ?? textInkOnFill(color), hoverUnderline: true }
}

/** La face d'une action déjà faite : plate, dans la tinte du succès et son encre. */
const DONE_FACE: PushFace = { fill: "successLight", border: null, relief: null, ink: "success-strong", settled: true }

export function Button(props: ButtonProps) {
    const { variant = "solid", color = "role-fill", textColor, done = false, ...shell } = props
    if (done) return <PushButton {...shell} iconName="check" face={DONE_FACE} />
    return <PushButton {...shell} face={actionFace(variant, color, textColor)} />
}

export type PushButtonProps = Omit<ButtonProps, "variant" | "color" | "textColor" | "done"> & {
    face: PushFace
    /** `button` par défaut ; une pilule de sélection est un `radio`. */
    accessibilityRole?: "button" | "radio"
    /** L'état choisi d'une pilule de sélection — dit aux lecteurs d'écran (`aria-checked`). */
    selected?: boolean
    /**
     * Un poussoir carré, son glyphe seul (`iconName`) : le libellé ne se lit plus qu'au lecteur d'écran. La bascule
     * Dialogue · Concentré posée sur la vidéo d'un élève du live (`<TutorSoundModeToggle>`). Défaut `false`.
     */
    iconOnly?: boolean
    /** La zone de toucher, au-delà de la face (un poussoir de 32 px dans une vidéo). Défaut : la face. */
    hitSlop?: number
}

/** Le poussoir, sans intention : il peint la face qu'on lui donne. Voir `PushFace`. */
export function PushButton(props: PushButtonProps) {
    const {
        label,
        face,
        size = "md",
        fullWidth = false,
        fitContent = true,
        iconName,
        iconPosition = "left",
        onPress,
        disabled = false,
        loading = false,
        testID,
        accessibilityLabel,
        accessibilityHint,
        accessibilityRole = "button",
        selected,
        underlay,
        iconOnly = false,
        hitSlop,
    } = props

    const { theme } = useUnistyles()
    const dimensions = useContext(DimensionsContext) ?? Dimensions.get("window")

    const fg = face.ink
    const dims = SIZE_TOKENS[size]
    const isBlocked = disabled || loading
    const relief = face.relief

    const glyph = iconName != null ? <Icon name={iconName} size={dims.icon} color={fg} /> : null

    return (
        <Pressable
            testID={testID}
            onPress={onPress}
            disabled={isBlocked}
            accessibilityRole={accessibilityRole}
            accessibilityLabel={accessibilityLabel ?? label}
            accessibilityHint={accessibilityHint}
            accessibilityState={{ disabled: isBlocked }}
            hitSlop={hitSlop}
            // `aria-checked` et non `accessibilityState` : react-native-web 0.20 ignore ce dernier.
            aria-checked={selected}
            style={[
                styles.root(dims.height, dims.relief, isBlocked && face.settled !== true, fullWidth),
                focusRingHost,
                !fitContent && {
                    width: dimensions.width < 800 ? dimensions.width - 40 : dimensions.width / 2,
                },
            ]}
        >
            {({ pressed, hovered, focused }: PressableVisualState) => (
                <>
                    {relief != null && !(pressed && !isBlocked) && <View style={styles.relief(relief)} />}
                    <FocusRing visible={focused === true && !isBlocked} radius={theme.radius.md} />
                    <View
                        style={styles.face(
                            face.fill,
                            face.border,
                            dims.height,
                            iconOnly ? 0 : dims.paddingH,
                            pressed && !isBlocked ? dims.relief : 0,
                            iconOnly
                        )}
                    >
                        {underlay != null && (
                            <View style={styles.underlay} pointerEvents="none">
                                {underlay}
                            </View>
                        )}
                        {face.hoverVeil === true && hovered === true && !isBlocked && (
                            <View style={styles.hoverVeil} pointerEvents="none" />
                        )}
                        <Row gap="2" justify="center" align="center">
                            {iconPosition === "left" && !(iconOnly && loading) && glyph}
                            {loading ? (
                                <ActivityIndicator size="small" color={theme.colors[fg]} />
                            ) : iconOnly ? null : (
                                <Text
                                    size={dims.text}
                                    center
                                    weight="bold"
                                    color={fg}
                                    style={[
                                        styles.label,
                                        face.hoverUnderline === true && hovered === true ? styles.underline : undefined,
                                        face.strike === true ? styles.strike : undefined,
                                    ]}
                                >
                                    {label}
                                </Text>
                            )}
                            {iconPosition === "right" && !(iconOnly && loading) && glyph}
                        </Row>
                    </View>
                </>
            )}
        </Pressable>
    )
}

const styles = StyleSheet.create((theme) => ({
    /**
     * La boîte extérieure ne peint RIEN : elle réserve `hauteur + relief` une fois pour toutes,
     * pour que presser ne déplace rien autour du bouton. Ce qui se voit est peint par la couche
     * de relief et par la face, qui savent toutes deux disparaître.
     */
    root: (height: number, reliefHeight: number, disabled: boolean, fullWidth: boolean) => ({
        width: fullWidth ? ("100%" as const) : undefined,
        height: height + reliefHeight,
        opacity: disabled ? 0.5 : 1,
    }),
    /**
     * La couche de relief occupe toute la boîte et non sa seule bande du bas : une bande de 4 px
     * ne peut pas porter le rayon de 12 du bouton (il serait rogné à 2), et ses coins du bas ne
     * suivraient plus ceux de la face. La face, opaque, n'en laisse voir que la bande du bas.
     */
    relief: (color: SemanticColorKey) => ({
        ...RNStyleSheet.absoluteFillObject,
        borderRadius: theme.radius.md,
        backgroundColor: theme.colors[color],
    }),
    /** La face du bouton — en haut au repos, descendue de `offsetY` quand on presse. */
    face: (
        fill: SemanticColorKey | null,
        border: SemanticColorKey | null,
        height: number,
        paddingH: number,
        offsetY: number,
        square: boolean
    ) => ({
        height,
        width: square ? height : undefined,
        marginTop: offsetY,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        paddingHorizontal: paddingH,
        borderRadius: theme.radius.md,
        overflow: "hidden" as const,
        backgroundColor: fill != null ? theme.colors[fill] : "transparent",
        borderWidth: border != null ? OUTLINE_WIDTH : 0,
        borderColor: border != null ? theme.colors[border] : undefined,
    }),
    /**
     * « Survol : plus clair » — un voile blanc sur l'aplat, l'encre du libellé intacte. Le blanc
     * qui ne s'inverse pas : sur la nuit, `text-on-primary` est marine, et le voile assombrirait.
     */
    hoverVeil: {
        ...RNStyleSheet.absoluteFillObject,
        backgroundColor: theme.colors.white,
        opacity: HOVER_VEIL_OPACITY,
    },
    underlay: { ...RNStyleSheet.absoluteFillObject },
    label: { letterSpacing: LABEL_TRACKING },
    underline: { textDecorationLine: "underline" as const },
    strike: { textDecorationLine: "line-through" as const },
}))
